import { supabase } from '../client'
import type { CloudTask } from '../types'
import { logSupabaseError } from './logSupabaseError'

/* ── TASKS (Cloud) ─────────────────────────────────────────── */

/**
 * Get all tasks for user
 */
export async function getCloudTasks(userId: string, since?: Date): Promise<CloudTask[]> {
  if (!supabase) return []

  try {
    let query = supabase
      .from('tasks')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('order', { ascending: true })

    if (since) {
      query = query.gte('updated_at', since.toISOString())
    }

    const { data, error } = await query

    if (error) {
      logSupabaseError('Get tasks error', error)
      return []
    }

    return data || []
  } catch (error) {
    logSupabaseError('Get tasks error', error)
    return []
  }
}

/*
  `getCloudSyncCounts` (RPC `get_sync_counts`) stand hier bis zum 20.09.2026 und
  ist durch `getCloudSyncState` ersetzt: Zählwerte allein sagen nichts über eine
  GEÄNDERTE Zeile, die Zahl bleibt dabei gleich.

  Die SQL-Funktion `get_sync_counts` bleibt in der Datenbank stehen, bis keine
  alten Clients mehr unterwegs sind — deren Bundle ruft sie weiterhin. Hier wird
  sie nicht mehr gebraucht.
*/

/** Zählstand und jüngster Zeitstempel einer Tabelle in der Cloud. */
export interface CloudEntityState {
  /** Lebende Zeilen (`deleted_at IS NULL`) — vergleicht sich mit dem lokalen Bestand. */
  count: number
  /**
   * `max(updated_at)` über ALLE Zeilen, auch die gelöschten (ein Soft-Delete ist
   * ein UPDATE und hebt es mit an). `null` heißt: Die Tabelle ist komplett leer.
   */
  maxUpdatedAt: string | null
}

export type CloudSyncState = Record<
  | 'task' | 'subtask' | 'project' | 'section' | 'tag' | 'goal'
  | 'habit' | 'habitLog' | 'rewardUsage' | 'preference' | 'canvasNote' | 'timeEntry' | 'taskComment' | 'projectComment' | 'taskReminder',
  CloudEntityState
>

function readEntityState(row: Record<string, any>, prefix: string): CloudEntityState {
  return {
    count: Number(row[`${prefix}_count`] ?? 0),
    maxUpdatedAt: (row[`${prefix}_max_updated_at`] as string | null) ?? null,
  }
}

/**
 * Die **Vorprüfung**: eine Frage für alle zwölf Tabellen.
 *
 * Liefert je Tabelle Zählstand und jüngsten Zeitstempel, damit der Client danach
 * nur noch die Tabellen holt, bei denen sich daran etwas geändert hat. Ein Lauf
 * ohne Änderung kostet so einen Request statt 27.
 *
 * `null` heißt „keine Antwort" (alter Server ohne die RPC, Netzfehler) — der
 * Aufrufer fällt dann auf die volle Runde zurück. Siehe ADR 0027.
 */
export async function getCloudSyncState(userId: string): Promise<CloudSyncState | null> {
  if (!supabase) return null

  try {
    const { data, error } = await supabase.rpc('get_sync_state', { p_user_id: userId })

    if (error || !data?.[0]) {
      logSupabaseError('Get sync state error', error)
      return null
    }

    const row = data[0]
    return {
      task: readEntityState(row, 'task'),
      subtask: readEntityState(row, 'subtask'),
      project: readEntityState(row, 'project'),
      section: readEntityState(row, 'section'),
      tag: readEntityState(row, 'tag'),
      goal: readEntityState(row, 'goal'),
      habit: readEntityState(row, 'habit'),
      habitLog: readEntityState(row, 'habit_log'),
      rewardUsage: readEntityState(row, 'reward_usage'),
      preference: readEntityState(row, 'preference'),
      canvasNote: readEntityState(row, 'canvas_note'),
      timeEntry: readEntityState(row, 'time_entry'),
      /*
        Ein Server ohne die Migration #182 kennt das Paar nicht — `readEntityState` liest
        dann 0 und `null`, und die Vorprüfung überspringt die (dort nicht vorhandene) Tabelle.
      */
      taskComment: readEntityState(row, 'task_comment'),
      // Ebenso ohne die Migration #184.
      projectComment: readEntityState(row, 'project_comment'),
      // Ebenso ohne die Migration der Erinnerungen.
      taskReminder: readEntityState(row, 'task_reminder'),
    }
  } catch (error) {
    logSupabaseError('Get sync state error', error)
    return null
  }
}

/**
 * Get all cloud task IDs for a user (lightweight — only fetches id column).
 * Used for ID-set reconciliation to detect tasks missing from local DB.
 */
export async function getCloudTaskIds(userId: string): Promise<string[]> {
  if (!supabase) return []

  try {
    const { data, error } = await supabase
      .from('tasks')
      .select('id')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .limit(10000)

    if (error) {
      logSupabaseError('Get task IDs error', error)
      return []
    }

    return (data || []).map(row => row.id)
  } catch (error) {
    logSupabaseError('Get task IDs error', error)
    return []
  }
}

/**
 * Get specific cloud tasks by their IDs.
 * Used for targeted fetch of tasks missing from local DB.
 */
export async function getCloudTasksByIds(userId: string, ids: string[]): Promise<CloudTask[]> {
  if (!supabase || ids.length === 0) return []

  try {
    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .eq('user_id', userId)
      .in('id', ids)
      .is('deleted_at', null)

    if (error) {
      logSupabaseError('Get tasks by IDs error', error)
      return []
    }

    return data || []
  } catch (error) {
    logSupabaseError('Get tasks by IDs error', error)
    return []
  }
}

/**
 * Create a new task
 */
export async function createCloudTask(
  userId: string,
  task: Omit<CloudTask, 'id' | 'user_id' | 'created_at' | 'updated_at'> & { id?: string }
): Promise<CloudTask | null> {
  if (!supabase) return null

  try {
    const { data, error } = await supabase
      .from('tasks')
      .insert({
        ...task,
        user_id: userId,
      })
      .select()
      .single()

    if (error) {
      // Eine umgewandelte Unteraufgabe übernimmt eine Kennung (T14 #179). Hat ein anderes
      // Gerät desselben Kontos die Zeile schon angelegt, ist es dieselbe — die nehmen.
      if (error.code === '23505' && task.id) {
        const { data: existing } = await supabase.from('tasks').select().eq('id', task.id).eq('user_id', userId).maybeSingle()
        if (existing) return existing as CloudTask
      }
      logSupabaseError('Create task error', error)
      return null
    }

    return data
  } catch (error) {
    logSupabaseError('Create task error', error)
    return null
  }
}

/**
 * Update a task. Returns the server-assigned updated_at timestamp on success,
 * or null on failure.
 */
export async function updateCloudTask(
  taskId: string,
  updates: Partial<Omit<CloudTask, 'id' | 'user_id' | 'created_at'>>,
  userId?: string,
): Promise<{ updated_at: string } | null> {
  if (!supabase) return null

  try {
    let query = supabase
      .from('tasks')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', taskId)
    if (userId) query = query.eq('user_id', userId)
    const { data, error } = await query
      .select('updated_at')
      .single()

    if (error) {
      logSupabaseError('Update task error', error)
      return null
    }

    return data
  } catch (error) {
    logSupabaseError('Update task error', error)
    return null
  }
}

/**
 * Delete a task (soft delete)
 */
export async function deleteCloudTask(taskId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false

  try {
    let query = supabase
      .from('tasks')
      .update({
        deleted_at: new Date().toISOString(),
      })
      .eq('id', taskId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query

    if (error) {
      logSupabaseError('Delete task error', error)
      return false
    }

    return true
  } catch (error) {
    logSupabaseError('Delete task error', error)
    return false
  }
}

/**
 * Get cloud tasks that were deleted since a given date
 */
export async function getDeletedCloudTasks(userId: string, since?: Date): Promise<{ id: string; local_id: string | null; updated_at: string }[]> {
  if (!supabase) return []

  try {
    let query = supabase
      .from('tasks')
      .select('id, local_id, updated_at')
      .eq('user_id', userId)
      .not('deleted_at', 'is', null)

    if (since) {
      query = query.gte('deleted_at', since.toISOString())
    }

    const { data, error } = await query

    if (error) {
      logSupabaseError('Get deleted tasks error', error)
      return []
    }

    return data || []
  } catch (error) {
    logSupabaseError('Get deleted tasks error', error)
    return []
  }
}

/**
 * Hard delete a task
 */
export async function hardDeleteCloudTask(taskId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false

  try {
    let query = supabase.from('tasks').delete().eq('id', taskId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query

    if (error) {
      logSupabaseError('Hard delete task error', error)
      return false
    }

    return true
  } catch (error) {
    logSupabaseError('Hard delete task error', error)
    return false
  }
}
